import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, num } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Card, Badge, Loading } from "@/components/kit";
import {
  LayoutDashboard, Store, Package, TrendingUp, Target, AlertTriangle,
  ChevronRight, RefreshCw, Eye, Settings, Plus, ArrowLeft, ArrowRight, Mail
} from "lucide-react";

export default function OrgDashboard() {
  const { user, activeStore, setActiveStore, hasFeature, stores } = useAuth();
  const navigate = useNavigate();

  const [orgStores, setOrgStores] = useState([]);
  const [overview, setOverview] = useState(null);
  const [purchasing, setPurchasing] = useState(null);
  const [accuracy, setAccuracy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");

  const loadAll = async () => {
    setLoading(true);
    try {
      const [storesRes, overviewRes, purchasingRes, accuracyRes] = await Promise.all([
        api.get("/org/stores"),
        api.get("/org/overview"),
        api.get("/org/purchasing"),
        api.get("/org/accuracy"),
      ]);
      setOrgStores(storesRes.data.stores);
      setOverview(overviewRes.data);
      setPurchasing(purchasingRes.data);
      setAccuracy(accuracyRes.data);
    } catch (e) {
      toast.error("Failed to load org dashboard");
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!hasFeature("multi_store")) {
      navigate("/app");
      return;
    }
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasFeature]);

  if (!hasFeature("multi_store")) return null;

  const handleSwitchStore = (storeId) => {
    setActiveStore(storeId);
    navigate("/app");
  };

  if (loading) return <Loading />;

  return (
    <div className="max-w-7xl mx-auto px-6 py-8 space-y-6">
      {/* Header with store switcher */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <PageHeader
            title="Multi-Site Dashboard"
            subtitle={`${orgStores.length} locations · ${overview?.totals?.store_count || 0} active stores`}
          />
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <select
              value={activeStore}
              onChange={(e) => handleSwitchStore(e.target.value)}
              className="appearance-none bg-white border border-zinc-200 px-4 py-2 pr-10 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900"
              aria-label="Switch store"
            >
              {orgStores.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name} ({s.city})
                </option>
              ))}
            </select>
            <ChevronRight className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400 pointer-events-none" />
          </div>
          <button onClick={loadAll} className="border border-zinc-200 px-4 py-2 rounded-lg text-sm font-medium hover:bg-zinc-50 transition-colors">
            <RefreshCw className="h-4 w-4 inline mr-1" /> Refresh
          </button>
        </div>
      </div>

      {/* Tab navigation */}
      <div className="border-b border-zinc-200">
        <nav className="flex gap-1 overflow-x-auto" role="tablist">
          {[
            { id: "overview", label: "Overview", icon: LayoutDashboard },
            { id: "purchasing", label: "Consolidated Purchasing", icon: Package },
            { id: "accuracy", label: "Accuracy Comparison", icon: Target },
            { id: "stores", label: "Store Management", icon: Store },
          ].map((tab) => (
            <button
              key={tab.id}
              role="tab"
              aria-selected={activeTab === tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 rounded-t-lg transition-colors whitespace-nowrap ${
                activeTab === tab.id
                  ? "border-zinc-900 text-zinc-900"
                  : "border-transparent text-zinc-500 hover:text-zinc-700 hover:bg-zinc-50"
              }`}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab panels */}
      {activeTab === "overview" && <OverviewTab overview={overview} />}
      {activeTab === "purchasing" && <PurchasingTab purchasing={purchasing} />}
      {activeTab === "accuracy" && <AccuracyTab accuracy={accuracy} />}
      {activeTab === "stores" && <StoresTab stores={orgStores} onRefresh={loadAll} />}
    </div>
  );
}

function OverviewTab({ overview }) {
  if (!overview) return <div className="py-12 text-center text-zinc-500">Loading overview...</div>;

  return (
    <div className="space-y-6">
      {/* Totals cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs font-semibold uppercase tracking-[0.08em] text-zinc-500">Total Locations</div>
              <div className="mt-1 font-display text-3xl font-semibold tnum">{overview.totals.store_count}</div>
            </div>
            <div className="h-10 w-10 rounded-lg bg-zinc-100 grid place-items-center">
              <Store className="h-5 w-5 text-zinc-600" />
            </div>
          </div>
        </Card>
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs font-semibold uppercase tracking-[0.08em] text-zinc-500">Predicted Orders</div>
              <div className="mt-1 font-display text-3xl font-semibold tnum">{num(overview.totals.predicted_transactions)}</div>
            </div>
            <div className="h-10 w-10 rounded-lg bg-emerald-50 grid place-items-center">
              <TrendingUp className="h-5 w-5 text-emerald-600" />
            </div>
          </div>
        </Card>
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs font-semibold uppercase tracking-[0.08em] text-zinc-500">Predicted Revenue</div>
              <div className="mt-1 font-display text-3xl font-semibold tnum">NZ${num(overview.totals.predicted_revenue)}</div>
            </div>
            <div className="h-10 w-10 rounded-lg bg-amber-50 grid place-items-center">
              <Target className="h-5 w-5 text-amber-600" />
            </div>
          </div>
        </Card>
      </div>

      {/* Per-store breakdown */}
      <Card className="p-0 overflow-hidden">
        <div className="px-6 py-4 border-b border-zinc-200">
          <h3 className="font-medium text-zinc-900">Store Breakdown for {(new Date(overview.stores[0]?.forecast?.date || Date.now())).toLocaleDateString()}</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50">
              <tr>
                <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-[0.08em] text-zinc-500">Store</th>
                <th className="px-5 py-3 text-right text-xs font-semibold uppercase tracking-[0.08em] text-zinc-500">Orders</th>
                <th className="px-5 py-3 text-right text-xs font-semibold uppercase tracking-[0.08em] text-zinc-500">Revenue</th>
                <th className="px-5 py-3 text-right text-xs font-semibold uppercase tracking-[0.08em] text-zinc-500">Confidence</th>
                <th className="px-5 py-3 text-right text-xs font-semibold uppercase tracking-[0.08em] text-zinc-500">Low Stock</th>
                <th className="px-5 py-3 text-right text-xs font-semibold uppercase tracking-[0.08em] text-zinc-500">Purchasing Alerts</th>
                <th className="px-5 py-3 text-right"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {overview.stores.map((s) => (
                <tr key={s.store_id} className="hover:bg-zinc-50/50">
                  <td className="px-5 py-4">
                    <div className="font-medium text-zinc-900">{s.store_name}</div>
                    <div className="text-xs text-zinc-400">{s.city}</div>
                  </td>
                  <td className="px-5 py-4 text-right font-medium tnum">{num(s.forecast.predicted_transactions)}</td>
                  <td className="px-5 py-4 text-right font-medium tnum">NZ${num(s.forecast.predicted_revenue)}</td>
                  <td className="px-5 py-4 text-right">
                    <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${s.forecast.confidence >= 0.7 ? "bg-emerald-100 text-emerald-700" : s.forecast.confidence >= 0.5 ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"}`}>
                      {Math.round(s.forecast.confidence * 100)}%
                    </div>
                  </td>
                  <td className="px-5 py-4 text-right">
                    {s.low_stock_count > 0 ? (
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
                        <AlertTriangle className="h-3 w-3" /> {s.low_stock_count}
                      </span>
                    ) : (
                      <span className="text-xs text-zinc-400">—</span>
                    )}
                  </td>
                  <td className="px-5 py-4 text-right">
                    {s.purchasing_alerts.length > 0 ? (
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                        {s.purchasing_alerts.length} items
                      </span>
                    ) : (
                      <span className="text-xs text-zinc-400">—</span>
                    )}
                  </td>
                  <td className="px-5 py-4 text-right">
                    <Link to={`/app/forecast?store=${s.store_id}`} className="text-amber-700 hover:underline text-sm font-medium">
                      View <ArrowRight className="h-3.5 w-3.5 inline" />
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function PurchasingTab({ purchasing }) {
  if (!purchasing) return <div className="py-12 text-center text-zinc-500">Loading purchasing...</div>;

  if (!purchasing.suppliers.length) {
    return (
      <Card className="p-12 text-center">
        <Package className="h-12 w-12 mx-auto text-zinc-300" />
        <h3 className="mt-4 font-medium text-zinc-900">No purchasing needed</h3>
        <p className="mt-2 text-sm text-zinc-500">All stores have sufficient stock for tomorrow's predicted demand.</p>
        <p className="mt-1 text-xs text-zinc-400">Generated for {purchasing.generated_for}</p>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-medium text-zinc-900">Consolidated Purchase Orders for {purchasing.generated_for}</h3>
        <span className="text-xs text-zinc-500">{purchasing.suppliers.length} suppliers</span>
      </div>
      {purchasing.suppliers.map((sup) => (
        <Card key={sup.supplier.id} className="p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-lg bg-amber-50 grid place-items-center">
                <Package className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <div className="font-medium text-zinc-900">{sup.supplier.name}</div>
                <div className="text-xs text-zinc-500 flex items-center gap-2">
                  <span>{sup.total_items} products</span>
                  <span>·</span>
                  <span>{sup.stores_affected} store{sup.stores_affected !== 1 ? "s" : ""}</span>
                  {sup.supplier.lead_time_days && <span>· {sup.supplier.lead_time_days}d lead time</span>}
                </div>
              </div>
            </div>
            {sup.supplier.contact && sup.supplier.contact.includes("@") && (
              <a href={`mailto:${sup.supplier.contact}`} className="text-sm text-amber-700 hover:underline flex items-center gap-1">
                <Mail className="h-3.5 w-3.5" /> Email PO
              </a>
            )}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs font-semibold uppercase tracking-[0.08em] text-zinc-500 border-b border-zinc-100">
                  <th className="py-2">Store</th>
                  <th className="py-2">Product</th>
                  <th className="py-2 text-right">Qty</th>
                  <th className="py-2 text-right">Unit</th>
                  <th className="py-2 text-right">Current Stock</th>
                  <th className="py-2 text-right">Min Stock</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-50">
                {sup.items.map((item, idx) => (
                  <tr key={idx} className="hover:bg-zinc-50/50">
                    <td className="py-2 text-zinc-600">{item.store_name}</td>
                    <td className="py-2 font-medium text-zinc-900">{item.product}</td>
                    <td className="py-2 text-right font-medium tnum">{num(item.quantity)}</td>
                    <td className="py-2 text-right text-zinc-500">{item.unit}</td>
                    <td className="py-2 text-right tnum">{num(item.current_stock)}</td>
                    <td className="py-2 text-right text-zinc-400 tnum">{num(item.min_stock)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      ))}
    </div>
  );
}

function AccuracyTab({ accuracy }) {
  if (!accuracy) return <div className="py-12 text-center text-zinc-500">Loading accuracy...</div>;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-4">
        <h3 className="font-medium text-zinc-900">Location-by-Location Accuracy ({accuracy.window_days}d window)</h3>
        <select
          value={accuracy.metric}
          onChange={(e) => window.location.href = `/app/org?tab=accuracy&metric=${e.target.value}`}
          className="border border-zinc-200 px-3 py-2 rounded-lg text-sm"
        >
          <option value="transactions">Orders</option>
          <option value="revenue">Revenue</option>
          <option value="coffee">Coffee</option>
          <option value="food">Food</option>
        </select>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {accuracy.stores.map((s) => (
          <Card key={s.store_id} className="p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="font-medium text-zinc-900">{s.store_name}</div>
                <div className="text-xs text-zinc-400">{s.city}</div>
              </div>
              <Eye className="h-5 w-5 text-zinc-400" />
            </div>

            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <MetricBox label="MAPE (30d)" value={`${s.metrics.mape?.toFixed(1) || "—"}%`} />
                <MetricBox label="MAPE (7d)" value={`${s.metrics_7d.mape?.toFixed(1) || "—"}%`} />
                <MetricBox label="MAE" value={num(s.metrics.mae || 0)} />
                <MetricBox label="Bias" value={num(s.metrics.bias || 0)} />
              </div>
            </div>

            {s.series.length > 0 && (
              <div className="mt-4 h-32 relative">
                <SimpleSparkline
                  data={s.series.map((p) => ({ x: p.date, y: p.actual, y2: p.predicted }))}
                  color="#3f3f46"
                  color2="#f59e0b"
                />
              </div>
            )}
          </Card>
        ))}
      </div>
    </div>
  );
}

function MetricBox({ label, value }) {
  return (
    <div className="p-3 bg-zinc-50 rounded-lg">
      <div className="text-[10px] font-semibold uppercase tracking-[0.08em] text-zinc-500">{label}</div>
      <div className="mt-1 font-display text-xl font-semibold tnum text-zinc-900">{value}</div>
    </div>
  );
}

function SimpleSparkline({ data, color, color2 }) {
  if (!data.length) return null;
  const width = 100;
  const height = 28;
  const max = Math.max(...data.map(d => Math.max(d.y || 0, d.y2 || 0)));
  const min = Math.min(...data.map(d => Math.min(d.y || 0, d.y2 || 0)));
  const range = max - min || 1;
  const xStep = width / (data.length - 1 || 1);

  const path = data.map((d, i) => {
    const x = i * xStep;
    const y = height - ((d.y - min) / range) * height;
    return `${i === 0 ? "M" : "L"} ${x} ${y}`;
  }).join(" ");

  const path2 = data.map((d, i) => {
    const x = i * xStep;
    const y = height - ((d.y2 - min) / range) * height;
    return `${i === 0 ? "M" : "L"} ${x} ${y}`;
  }).join(" ");

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-full" aria-hidden="true">
      <path d={path} stroke={color} strokeWidth="1.5" fill="none" opacity="0.5" />
      <path d={path2} stroke={color2} strokeWidth="1.5" fill="none" strokeDasharray="4 3" />
    </svg>
  );
}

function StoresTab({ stores, onRefresh }) {
  return (
    <Card className="p-0 overflow-hidden">
      <div className="px-6 py-4 border-b border-zinc-200 flex items-center justify-between">
        <h3 className="font-medium text-zinc-900">Manage Stores</h3>
        <span className="text-xs text-zinc-500">{stores.length} / 5 stores</span>
      </div>
      <div className="divide-y divide-zinc-100">
        {stores.map((s) => (
          <div key={s.id} className="px-6 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="h-10 w-10 rounded-lg bg-zinc-100 grid place-items-center">
                <Store className="h-5 w-5 text-zinc-600" />
              </div>
              <div>
                <div className="font-medium text-zinc-900">{s.name}</div>
                <div className="text-xs text-zinc-400 flex items-center gap-2">
                  <span>{s.city}, {s.country}</span>
                  <span>·</span>
                  <span>{s.product_count} products</span>
                  {s.last_sales_date && <span>· Last sales: {s.last_sales_date}</span>}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => window.open(`/app?store=${s.id}`, "_blank")}
                className="border border-zinc-200 px-3 py-1.5 rounded-lg text-xs font-medium hover:bg-zinc-50 transition-colors"
              >
                <Eye className="h-3.5 w-3.5 inline mr-1" /> View
              </button>
              <Link
                to="/app/settings"
                className="border border-zinc-200 px-3 py-1.5 rounded-lg text-xs font-medium hover:bg-zinc-50 transition-colors"
              >
                <Settings className="h-3.5 w-3.5 inline mr-1" /> Settings
              </Link>
            </div>
          </div>
        ))}
        {stores.length < 5 && (
          <div className="px-6 py-8 text-center border-t border-zinc-200">
            <div className="h-12 w-12 rounded-full border-2 border-dashed border-zinc-300 grid place-items-center mx-auto mb-3">
              <Plus className="h-6 w-6 text-zinc-400" />
            </div>
            <p className="text-sm text-zinc-600">Add up to {5 - stores.length} more location{stores.length === 4 ? "" : "s"}</p>
            <Link to="/app/settings" className="mt-3 inline-flex items-center gap-1.5 text-amber-700 font-semibold hover:underline">
              Go to Settings to add a store <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        )}
      </div>
    </Card>
  );
}